// src/app/components/foundation/WorkspaceActionItem.tsx
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';
import { Button } from './Button';

export type WorkspaceActionItemVariant = 'primary' | 'neutral' | 'danger';

export interface WorkspaceActionItemProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  label: ReactNode;
  variant?: WorkspaceActionItemVariant;
  className?: string;
}

// Variant classes carry ONLY the semantic tint (border/background/text
// color while enabled) -- sizing, padding, font, radius, shadow, and the
// disabled text color are exclusively owned by the ancestor
// `WorkspaceActions`, which targets every rendered `button` directly.
// `!`-prefixed so these deterministically win over the Foundation
// Button's own `ghost` color utilities regardless of Tailwind's
// generated stylesheet order.
const VARIANT_CLASSES: Record<WorkspaceActionItemVariant, string> = {
  primary:
    '!border-[var(--vania-accent-teal)]/30 !bg-[var(--vania-accent-teal)]/10 !text-[var(--vania-accent-teal)]',
  danger:
    '!border-[var(--vania-accent-rose)]/30 !bg-[var(--vania-accent-rose)]/10 !text-[var(--vania-accent-rose)]',
  neutral:
    '!border-[var(--vania-colors-border-subtle,rgba(255,255,255,0.08))] !bg-transparent !text-[var(--vania-colors-text-muted,#8b93a1)]',
};

/**
 * Shared compact action item for a `Workspace` action bar (`WorkspaceActions`).
 *
 * This is NOT a primary-CTA button replacement -- it's a small, horizontal,
 * SaaS-admin-toolbar-style action chip: never full width, and never meant
 * to be stacked vertically (the caller's container controls that, this
 * only styles the item's semantic tint). Height, padding, font-size,
 * font-weight, radius, shadow, and disabled appearance all come from the
 * ancestor `WorkspaceActions` -- this component never sets its own, so
 * there's exactly one place that can drift out of sync across features.
 * `variant` only changes the border/background/text tint (primary /
 * neutral / danger) while enabled; `dir` is inherited from an ancestor
 * (e.g. `WorkspaceActions`) rather than set here, so it stays
 * RTL-compatible without hardcoding direction.
 *
 * Renders through the shared Foundation `Button` primitive (as an inert
 * `ghost`-variant base) so every button in the app shares one underlying
 * element and interaction/focus/disabled behavior, while this component's
 * own public API and tinted-variant palette stay unchanged for every
 * existing caller.
 *
 * Purely presentational: no business logic, no API calls, no assumptions
 * about handlers. `onClick` and all other native `<button>` props/
 * `disabled` pass straight through untouched.
 */
export function WorkspaceActionItem({
  label,
  variant = 'neutral',
  disabled,
  className,
  type = 'button',
  ...rest
}: WorkspaceActionItemProps) {
  return (
    <Button
      variant="ghost"
      type={type}
      disabled={disabled}
      className={clsx('border', VARIANT_CLASSES[variant], className)}
      {...rest}
    >
      {label}
    </Button>
  );
}
